/**
 * Mock Data: Report Detail
 * Mock data for the report detail table.
 *
 * Corresponds to Postman endpoint: GET /product/api/v1/dashboard/summary-details
 */

import type { ReportDetailItem, ReportDetailApiResponse } from '../data/remote/response';

// Re-export types from response module for backward compatibility
export type { ReportDetailItem, ReportDetailData, ReportDetailApiResponse, PaginationInfo } from '../data/remote/response';

// Legacy aliases for backward compatibility
export type ReportDetailPaging = import('../data/remote/response').PaginationInfo;
export type ReportDetailMeta = import('../data/remote/response').ApiMeta;
export type ReportDetailResponse = ReportDetailApiResponse;

const monthNames = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December"
];

const generateMockItems = (): ReportDetailItem[] => {
  const baseItems: ReportDetailItem[] = [
    {
      reportId: "rep-2025-11",
      period: "2025-11",
      year: 2025,
      month: 11,
      monthName: "November",
      totalActivity: 29,
      totalEarned: 590000,
      voucherRedeemed: 7,
      newUserRegistered: 8,
      totalUser: 17,
      actions: {
        canDownload: true,
        canEdit: false,
      },
    },
    {
      reportId: "rep-2025-10",
      period: "2025-10",
      year: 2025,
      month: 10,
      monthName: "October",
      totalActivity: 12,
      totalEarned: 510000,
      voucherRedeemed: 3,
      newUserRegistered: 6,
      totalUser: 9,
      actions: {
        canDownload: true,
        canEdit: false,
      },
    },
  ];

  const items: ReportDetailItem[] = [...baseItems];
  const targetTotalRecords = 120;

  for (let year = 2025; year >= 2016 && items.length < targetTotalRecords; year--) {
    for (let monthIndex = 11; monthIndex >= 0 && items.length < targetTotalRecords; monthIndex--) {
      const month = monthIndex + 1;
      const monthStr = month.toString().padStart(2, '0');
      const period = `${year}-${monthStr}`;
      if (items.some((item) => item.period === period)) {
        continue;
      }

      const index = items.length;
      const totalActivity = Math.max(5, 30 - index);

      items.push({
        reportId: `rep-${period}`,
        period,
        year,
        month,
        monthName: monthNames[monthIndex],
        totalActivity,
        totalEarned: 500000 + (index * 10000),
        voucherRedeemed: Math.max(1, 10 - Math.floor(index / 10)),
        newUserRegistered: Math.max(1, 10 - Math.floor(index / 12)),
        totalUser: 100 + index * 5,
        actions: {
          canDownload: true,
          canEdit: false,
        },
      });
    }
  }

  return items;
};

const reportDetailItems: ReportDetailItem[] = generateMockItems();

/**
 * Generate paginated mock data for report detail
 * @param page - Current page number (1-indexed)
 * @param pageSize - Number of items per page
 * @returns Paginated report detail response
 */
export const getMockReportDetailData = (page: number = 1, pageSize: number = 10): ReportDetailResponse => {
  const totalRecords = reportDetailItems.length;
  const totalPages = Math.ceil(totalRecords / pageSize);
  const currentPage = Math.min(Math.max(1, page), totalPages);
  
  const startIndex = (currentPage - 1) * pageSize;
  const endIndex = Math.min(startIndex + pageSize, totalRecords);
  const paginatedItems = reportDetailItems.slice(startIndex, endIndex);

  return {
    data: paginatedItems,
    paging: {
      currentPage,
      pageSize,
      totalRecords,
      totalPages,
      hasNext: currentPage < totalPages,
      hasPrevious: currentPage > 1,
    },
    meta: {
      code: "20000",
      message: "OK",
      timestamp: new Date().toISOString(),
    },
  };
};

// Default export for backward compatibility (page 1)
export const mockReportDetailData: ReportDetailResponse = getMockReportDetailData(1, 10);
